/**
 * RestroPulse POS — Sprint 8 (POS-QCS)
 *
 * QuantityElevationDialog · manager PIN authorisation surface used
 * when a cashier tries to raise a line quantity or an order's cover
 * count above the normal-operator ceiling (999 → up to 10,000).
 *
 * Design contract:
 *   • Every 1000+ change is a FRESH authorisation. The parent MUST
 *     obtain a new authorisation_id per action; a value approved for
 *     one line/order/kind cannot bypass a different one. The server
 *     enforces this in `_consume_authorisation`.
 *   • Values above 10,000 are hard-blocked here AND on the server —
 *     this dialog will refuse to submit them, and if a caller passes a
 *     value above the ceiling the dialog explains why and disables OK.
 *   • The dialog only ever opens ONCE per attempt — rapid + presses
 *     on a locked line surface a toast (from BillingScreen's throttle)
 *     rather than reopening this dialog.
 *   • On success the caller receives `authorisation_id`, which it
 *     forwards on the subsequent add-line / patch-line / open-order
 *     request. If the caller cancels, no server call is made.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { ShieldCheck, XCircle } from "lucide-react";

const NORMAL_MAX_QTY    = 999;
const MANAGER_MAX_QTY   = 10_000;
const NORMAL_MAX_COVERS = 999;
const MANAGER_MAX_COVERS = 10_000;

// ─── Shared limit copy ──────────────────────────────────────────────
export function limitsFor(kind) {
  return kind === "covers"
    ? { normal: NORMAL_MAX_COVERS, manager: MANAGER_MAX_COVERS, noun: "cover count" }
    : { normal: NORMAL_MAX_QTY,    manager: MANAGER_MAX_QTY,    noun: "quantity" };
}

/**
 * Fetch a fresh authorisation from the server. Returns
 * `{ ok, authorisation_id, approved_value, manager_name }` or throws.
 */
async function requestAuthorisation({
  API, api_headers, order_id, line_id, kind,
  approved_value, previous_value, pin, initiator_staff_id, reason,
}) {
  const terminal_id = _terminalId();
  const event_id = _mintEventId();
  const url = kind === "covers"
    ? `${API}/api/pos/orders/${order_id}/authorize-covers`
    : `${API}/api/pos/orders/${order_id}/lines/${line_id || "__NEW__"}/authorize-qty`;
  const body = kind === "covers"
    ? { event_id, terminal_id, approved_value, previous_value, manager_pin: pin,
        initiator_staff_id, reason }
    : { event_id, terminal_id, line_id: line_id || null,
        approved_value, previous_value, manager_pin: pin,
        initiator_staff_id, reason };
  const r = await axios.post(url, body, { headers: api_headers });
  return r.data;
}

// ─── Local helpers (mirror posLiveActions) ──────────────────────────
function _mintEventId() {
  // ULID-shape 26-char string — server accepts any 8..64-char opaque id.
  const b64 = () => Math.random().toString(36).slice(2, 12);
  return "ev_" + b64() + b64() + Date.now().toString(36);
}
function _terminalId() {
  try {
    const b = JSON.parse(window.localStorage.getItem("pos_terminal_binding") || "null");
    return b?.terminal_id || "unbound";
  } catch (_) { return "unbound"; }
}

export default function QuantityElevationDialog({
  open,
  kind = "quantity",            // "quantity" | "covers"
  intendedValue,                // target absolute value (qty or covers)
  previousValue = null,         // best-effort audit context
  order_id,                     // may be "__PENDING__" for open-order covers
  line_id = null,               // may be "__NEW__" or null for add-line/open-order
  initiator_staff_id = null,    // cashier who is requesting elevation
  API,                          // base URL, e.g. process.env.REACT_APP_BACKEND_URL
  headers = {},                 // auth headers (Bearer + terminal + etc)
  onCancel,
  onConfirm,                    // ({ authorisation_id, approved_value, manager_name }) => void
}) {
  const { normal, manager, noun } = limitsFor(kind);
  const [pin,    setPin]    = useState("");
  const [reason, setReason] = useState("");
  const [busy,   setBusy]   = useState(false);
  const [error,  setError]  = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPin(""); setReason(""); setError(null); setBusy(false);
    setTimeout(() => inputRef.current?.focus(), 40);
  }, [open, intendedValue, order_id, line_id, kind]);

  const outOfRange = intendedValue == null
    || Number(intendedValue) <= normal
    || Number(intendedValue) > manager;

  const submit = async () => {
    if (busy) return;
    if (!/^\d{4}$/.test((pin || "").trim())) {
      setError("Manager PIN is a four-digit code."); return;
    }
    setBusy(true); setError(null);
    try {
      const r = await requestAuthorisation({
        API, api_headers: headers,
        order_id, line_id, kind,
        approved_value: Number(intendedValue),
        previous_value: previousValue,
        pin: (pin || "").trim(),
        initiator_staff_id,
        reason: reason.trim() || null,
      });
      onConfirm({
        authorisation_id: r.authorisation_id,
        approved_value:   r.approved_value,
        manager_name:     r.manager_name,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Manager authorisation refused.";
      setError(typeof msg === "string" ? msg : "Manager authorisation refused.");
      setTimeout(() => inputRef.current?.focus(), 20);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel && onCancel(); }}>
      <DialogContent className="max-w-[440px] w-[92vw] p-0 gap-0 overflow-hidden"
                     data-testid="qty-elevation-dialog">
        <div className="px-4 py-3 border-b"
             style={{ background: "var(--pos-color-raised-surface)", borderColor: "var(--pos-color-border)" }}>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4"
                         style={{ color: "var(--pos-color-primary-text)" }} />
            <div className="text-[13px] font-semibold"
                 style={{ color: "var(--pos-color-primary-text)" }}>
              Manager approval required
            </div>
          </div>
          <div className="text-[11px] mt-1"
               style={{ color: "var(--pos-color-secondary-text)" }}>
            {kind === "covers"
              ? `Cover count above ${normal.toLocaleString()} requires a manager PIN. Cover count cannot exceed ${manager.toLocaleString()}.`
              : `Quantity above ${normal.toLocaleString()} requires a manager PIN. Maximum allowed quantity is ${manager.toLocaleString()}.`}
          </div>
        </div>

        {outOfRange ? (
          <div className="p-4">
            <div className="rounded border p-3 text-[12px]"
                 style={{
                   background: "#FEF2F2",
                   borderColor: "#FCA5A5",
                   color: "#7F1D1D",
                 }}
                 data-testid="qty-elevation-hard-block">
              <div className="flex items-center gap-1.5 font-semibold mb-1">
                <XCircle className="w-3.5 h-3.5" /> Cannot approve this value
              </div>
              {intendedValue != null && Number(intendedValue) > manager
                ? <>Maximum allowed {noun} is {manager.toLocaleString()}. Requested: {Number(intendedValue).toLocaleString()}.</>
                : <>This value does not need manager approval.</>}
            </div>
            <div className="mt-4 flex justify-end">
              <button type="button"
                      className="h-9 px-3 rounded text-[12px]"
                      style={{
                        background: "var(--pos-color-primary)",
                        color: "var(--pos-color-on-primary)",
                      }}
                      onClick={onCancel}
                      data-testid="qty-elevation-close">
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="rounded border p-3 text-[12px]"
                 style={{
                   background: "var(--pos-color-card-bg)",
                   borderColor: "var(--pos-color-border)",
                   color: "var(--pos-color-primary-text)",
                 }}>
              <div className="flex items-center justify-between">
                <span style={{ color: "var(--pos-color-secondary-text)" }}>
                  {kind === "covers" ? "Requested cover count" : "Requested quantity"}
                </span>
                <span className="tabular-nums font-semibold"
                      data-testid="qty-elevation-target-value">
                  {Number(intendedValue).toLocaleString()}
                </span>
              </div>
              {previousValue != null && (
                <div className="flex items-center justify-between mt-1">
                  <span style={{ color: "var(--pos-color-secondary-text)" }}>
                    Previous
                  </span>
                  <span className="tabular-nums"
                        style={{ color: "var(--pos-color-secondary-text)" }}>
                    {Number(previousValue).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="text-[11px] font-medium"
                     style={{ color: "var(--pos-color-secondary-text)" }}>
                Manager PIN
              </label>
              <input ref={inputRef}
                     type="password"
                     inputMode="numeric"
                     pattern="\d*"
                     maxLength={4}
                     value={pin}
                     onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                     onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                     className="mt-1 w-full h-10 rounded border px-3 text-[16px] tracking-[0.4em] tabular-nums text-center"
                     style={{
                       background: "var(--pos-color-card-bg)",
                       borderColor: "var(--pos-color-border)",
                       color: "var(--pos-color-primary-text)",
                     }}
                     placeholder="••••"
                     data-testid="qty-elevation-pin" />
            </div>

            <div>
              <label className="text-[11px] font-medium"
                     style={{ color: "var(--pos-color-secondary-text)" }}>
                Reason (optional)
              </label>
              <input type="text" value={reason}
                     onChange={(e) => setReason(e.target.value.slice(0, 120))}
                     className="mt-1 w-full h-9 rounded border px-3 text-[12px]"
                     style={{
                       background: "var(--pos-color-card-bg)",
                       borderColor: "var(--pos-color-border)",
                       color: "var(--pos-color-primary-text)",
                     }}
                     placeholder="Bulk catering order, etc."
                     data-testid="qty-elevation-reason" />
            </div>

            {error && (
              <div className="rounded border p-2 text-[12px]"
                   style={{
                     background: "#FEF2F2",
                     borderColor: "#FCA5A5",
                     color: "#7F1D1D",
                   }}
                   data-testid="qty-elevation-error">
                {error}
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button type="button"
                      onClick={onCancel}
                      disabled={busy}
                      className="h-10 px-3 rounded text-[12px]"
                      style={{
                        border: "1px solid var(--pos-color-border)",
                        color: "var(--pos-color-primary-text)",
                        background: "var(--pos-color-card-bg)",
                      }}
                      data-testid="qty-elevation-cancel">
                Cancel
              </button>
              <div className="flex-1" />
              <button type="button"
                      onClick={submit}
                      disabled={busy || !/^\d{4}$/.test(pin || "")}
                      className="h-10 px-4 rounded text-[12px] font-semibold"
                      style={{
                        background: "var(--pos-color-primary)",
                        color: "var(--pos-color-on-primary)",
                        opacity: (busy || !/^\d{4}$/.test(pin || "")) ? 0.55 : 1,
                        boxShadow: "var(--pos-shadow-sm)",
                      }}
                      data-testid="qty-elevation-confirm">
                {busy ? "Verifying…" : "Authorise"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
